import React from 'react';
import { XIcon, PackageIcon, CalendarIcon, CreditCardIcon, TruckIcon } from 'lucide-react';
interface ReturnItem {
  name: string;
  quantity: number;
  price: number;
  image: string;
}
interface ReturnData {
  id: string;
  originalOrderId: string;
  returnDate: string;
  status: 'approved' | 'pending' | 'rejected';
  reason: string;
  refundMethod: string;
  refundAmount: number;
  items: ReturnItem[];
  trackingNumber?: string;
  estimatedRefundDate?: string;
}
interface ReturnDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnData: ReturnData;
}
const STATUS_STYLES: Record<string, string> = {
  approved: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  rejected: 'bg-red-100 text-red-700'
};
export function ReturnDetailsModal({
  isOpen,
  onClose,
  returnData
}: ReturnDetailsModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-id="element-923" data-mp="qptiyboft3wl">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl" data-id="element-924" data-mp="17chgo5knedjg">
        <div className="sticky top-0 bg-[#1E2B55] text-white flex items-center justify-between p-5 z-10" data-id="element-925" data-mp="13bxiqzs935w9">
          <div data-id="element-926" data-mp="yahz9c5q17fz">
            <h2 className="text-lg font-bold" data-id="element-927" data-mp="ye5s093cdptn">{returnData.id}</h2>
            <p className="text-sm text-blue-200" data-id="element-928" data-mp="tcq6kfx95haq">
              Original Order: {returnData.originalOrderId}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" aria-label="Close" data-id="element-929" data-mp="jngev16bm0n5">
            <XIcon className="w-5 h-5" data-id="element-930" data-mp="fmmq65l20ht7" />
          </button>
        </div>

        <div className="p-5 space-y-5" data-id="element-931" data-mp="ana2xjq9t8q3">
          <div className="flex items-center justify-between" data-id="element-932" data-mp="aobdzo7adv7t">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${STATUS_STYLES[returnData.status] || 'bg-gray-100 text-gray-700'}`} data-id="element-933" data-mp="5nx6c79lknab">
              {returnData.status.charAt(0).toUpperCase() + returnData.status.slice(1)}
            </span>
            <div className="text-right" data-id="element-934" data-mp="3s68olr0imp9x">
              <p className="text-xs text-gray-500" data-id="element-935" data-mp="3s32zwcvstpeq">Refund Amount</p>
              <p className="text-xl font-bold text-[#1E2B55]" data-id="element-936" data-mp="3e790qlktueet">
                SAR {returnData.refundAmount.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-937" data-mp="9r6ggpyskb9o">
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg" data-id="element-938" data-mp="eqjca07n4soe">
              <CalendarIcon className="w-4 h-4 text-gray-500 mt-0.5" data-id="element-939" data-mp="esndgae5ndkz" />
              <div data-id="element-940" data-mp="ycuyasr4fnd">
                <p className="text-xs text-gray-500" data-id="element-941" data-mp="x1uokyilq10">Return Date</p>
                <p className="text-sm font-medium text-gray-900" data-id="element-942" data-mp="4zohg7gbwkrc">
                  {new Date(returnData.returnDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg" data-id="element-943" data-mp="2i5cyq1h9dceq">
              <CreditCardIcon className="w-4 h-4 text-gray-500 mt-0.5" data-id="element-944" data-mp="2d7tjozcqkvlq" />
              <div data-id="element-945" data-mp="2rxirvej8xzvi">
                <p className="text-xs text-gray-500" data-id="element-946" data-mp="2rwpqkt3242kr">Refund Method</p>
                <p className="text-sm font-medium text-gray-900" data-id="element-947" data-mp="2mxwb4v47m1hy">
                  {returnData.refundMethod}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg md:col-span-2" data-id="element-948" data-mp="1zaeofdzsxtlu">
              <PackageIcon className="w-4 h-4 text-gray-500 mt-0.5" data-id="element-949" data-mp="1ubv2vep4eju0" />
              <div data-id="element-950" data-mp="298cd22lkpsez">
                <p className="text-xs text-gray-500" data-id="element-951" data-mp="294okb5nyda1b">Reason</p>
                <p className="text-sm font-medium text-gray-900" data-id="element-952" data-mp="246veweuh4k23">
                  {returnData.reason}
                </p>
              </div>
            </div>
            {returnData.trackingNumber && <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg md:col-span-2" data-id="element-953" data-mp="3fm1xfmawvyjw">
                <TruckIcon className="w-4 h-4 text-blue-600 mt-0.5" data-id="element-954" data-mp="1p71aowlt7hjl" />
                <div data-id="element-955" data-mp="1u4ayy8xgpn5u">
                  <p className="text-xs text-gray-500" data-id="element-956" data-mp="1u5uhk66lcaas">Tracking Number</p>
                  <p className="text-sm font-medium text-blue-700" data-id="element-957" data-mp="1y5wgw8ousrmw">
                    {returnData.trackingNumber}
                  </p>
                </div>
              </div>}
            {returnData.estimatedRefundDate && <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg md:col-span-2" data-id="element-958" data-mp="2842h8lv0hppn">
                <CalendarIcon className="w-4 h-4 text-green-600 mt-0.5" data-id="element-959" data-mp="2d242ndfwu14a" />
                <div data-id="element-960" data-mp="2hvgcuijue5di">
                  <p className="text-xs text-gray-500" data-id="element-961" data-mp="2hux4ntjzuvco">Estimated Refund Date</p>
                  <p className="text-sm font-medium text-green-700" data-id="element-962" data-mp="2luxknimqrelu">
                    {new Date(returnData.estimatedRefundDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                  </p>
                </div>
              </div>}
          </div>

          <div data-id="element-963" data-mp="3113bxsrcvbe9">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-964" data-mp="311czl9oxh6gs">
              Returned Items
            </h3>
            <div className="space-y-3" data-id="element-965" data-mp="2rylnd7o8a6o3">
              {returnData.items.map((item, idx) => <div key={idx} className="flex gap-3 p-3 border border-gray-200 rounded-lg" data-id="element-966" data-mp="3ool4hqig2dia" data-mpi={idx}>
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" data-id="element-967" data-mp="3jqt3m6ztnh1y" />
                  <div className="flex-1" data-id="element-968" data-mp="3aw50amdt49fs">
                    <p className="font-medium text-gray-900 line-clamp-2" data-id="element-969" data-mp="3av3nacd8dcvf">
                      {item.name}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-970" data-mp="6j83y2onz24s">
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <p className="font-bold text-gray-900" data-id="element-971" data-mp="20rbxwu2h939o">
                    SAR {item.price.toFixed(2)}
                  </p>
                </div>)}
            </div>
          </div>

          <button onClick={onClose} className="w-full px-4 py-2.5 bg-[#1E2B55] text-white font-medium rounded-lg hover:bg-[#162041]" data-id="element-972" data-mp="1r0hc1qt585or">
            Close
          </button>
        </div>
      </div>
    </div>;
}